import { useCallback, useMemo } from 'react';

import { Asset, AssetType } from '@shared/api';
import { Big } from '@shared/safe-big';
import { assetService } from '@shared/services';
import { AppStore, UserStore } from '@shared/store';

import { useRates } from 'src/lib/rates/hooks';

const useMarkets = () => {
  const { assetsInitialized } = AppStore.useAppStore;
  const { userProfile } = UserStore.useUserStore;
  const { getRate } = useRates();

  /**
   * Memoized array of **all assets**, including those that are delisted.
   * Use this to access the complete list without filtering by status.
   *
   * @see assets - for the list of active assets only.
   */
  const allAssets = useMemo(() => {
    if (!assetsInitialized) return [];

    return assetService.getAllAssetList();
  }, [assetsInitialized]);

  /**
   * Memoized array of **active assets only**.
   * This list excludes any assets that have been delisted.
   *
   * @see allAssets - for the complete list including delisted assets.
   */
  const assets = useMemo(() => {
    if (!assetsInitialized) return [];

    return assetService.getActiveAssetList();
  }, [assetsInitialized]);

  const isAssetListed = useCallback(
    (assetCode: string) => {
      if (!assetsInitialized) return false;

      return assetService.getAssetByCode(assetCode) !== undefined;
    },
    [assetsInitialized],
  );

  const isFavourite = useCallback(
    (id: number) => {
      if (!userProfile?.userSettings.favouriteAssets) return false;

      return Object.keys(userProfile?.userSettings.favouriteAssets).includes(id.toString());
    },
    [userProfile?.userSettings.favouriteAssets],
  );

  const isNew = useCallback(
    (asset: Asset, maxAmount = 10) => {
      const newAssets = Array.from(assets)
        .sort((a, b) => Big(a.id).cmp(b.id))
        .slice(assets.length - maxAmount);

      return newAssets.find((a) => a.id === asset.id) !== undefined;
    },
    [assets],
  );

  const getAssetByName = useCallback(
    (name: string) => assets.find((a) => a.name.toLocaleLowerCase() === name.toLocaleLowerCase()),
    [assets],
  );
  const getAssetById = useCallback((id?: number) => allAssets.find((a) => a.id === id), [allAssets]);
  const getAssetByCode = useCallback((code?: string) => allAssets.find((a) => a.code === code), [allAssets]);
  const getAssetByUuid = useCallback((uuid?: string) => allAssets.find((a) => a.assetUuid === uuid), [allAssets]);

  const getAssetMarketCap = useCallback(
    (asset: Asset) => Big(asset.circulatingSupply).times(getRate(asset).midPrice),
    [getRate],
  );

  const getAssetsById = useCallback(
    (ids: number[]): Asset[] => ids.map((id) => getAssetById(id)).filter((asset) => asset !== undefined) as Asset[],
    [getAssetById],
  );

  const getAssetsByCode = useCallback(
    (codes: string[]): Asset[] =>
      codes.map((code) => getAssetByCode(code)).filter((asset) => asset !== undefined) as Asset[],
    [getAssetByCode],
  );

  const getAssetsByUuid = useCallback(
    (uuid: string[]): Asset[] =>
      uuid.map((assetUuid) => getAssetByUuid(assetUuid)).filter((asset) => asset !== undefined) as Asset[],
    [getAssetByUuid],
  );

  const getAssetsByCategoryId = useCallback(
    (categoryId: number): Asset[] => assets.filter((a) => a.categoryIds.includes(categoryId)),
    [assets],
  );

  const getBaseAssets = useCallback((): Asset[] => assets.filter((a) => a.isBaseAsset), [assets]);

  const isAssetFiat = useCallback((asset?: Asset | null) => asset?.assetType === AssetType.Fiat, []);

  const getAssetsByType = useCallback((type: AssetType) => assets.filter((a) => a.assetType === type), [assets]);

  return {
    allAssets,
    assets,
    isAssetListed,
    getAssetByName,
    getAssetById,
    getAssetByUuid,
    getAssetsById,
    getAssetsByCode,
    getAssetsByUuid,
    getAssetsByCategoryId,
    getAssetByCode,
    getBaseAssets,
    isFavourite,
    isNew,
    isAssetFiat,
    getAssetsByType,
    getAssetMarketCap,
  };
};

export { useMarkets };
